import { createSlice } from "@reduxjs/toolkit";
import { countryApi } from 'Services/CountryService';
import { organizationProfileApi } from 'Services/OrganizationProfileService';

export const initialState = {
    countryList: [],
    foreignStatesList: [],
    countyList: [],
    cityList: [],
    selectedCountry: {
        id: null,
        code: null,
        description: null,
        provinceOrStateList: []
    },
    selectedProvinceOrState: {
        id: null,
        code: null,
        description: null,
    },
    companyName: {
        value: "",
        error: "",
    },
    phoneNumber: {
        value: "",
        error: "",
    },
    businessEin: {
        value: "",
        error: "",
    },
    addressLine: {
        value: "",
        error: "",
    },
    addressLine2: {
        value: "",
        error: "",
    },
    city: {
        value: "",
        error: "",
    },
    provinceOrState: {
        value: "",
        error: "",
    },
    country: {
        value: "",
        error: "",
    },
    county: {
        value: "",
        error: "",
    },
    postalCode: {
        value: "",
        error: "",
    },
    showPageLeave: false,
    currentProfileInfo: {
        companyName: '',
        phoneNumber: '',
        businessEin: '',
        addressLine: '',
        addressLine2: '',
        country: '',
        provinceOrState: '',
        city: '',
        postalCode: '',
    }
};

const organizationProfileSlice = createSlice({
    name: "organization-profile",
    initialState,
    reducers: {
        handleChange: (state, action) => {
            state[action.payload.field].error = "";
            state[action.payload.field].value = action.payload.value;
        },
        handleModelErrors: (state, action) => {
            Object.entries(action.payload).forEach(([field, errors]) => { 
                state[field].error = errors[0];
            });
        },
        handleGet : (state, action) => {
            Object.entries(action.payload).forEach(([field, value]) => { 
                if (state[field]) {
                    state[field].value = value || '';
                    state[field].error = '';
                }
            });

            state.currentProfileInfo = action.payload;
        },
        handleChangeCountry: (state, action) => {
            state.selectedCountry = state.countryList.find(o => o.description === action.payload);
            state.country.value = state.countryList.find(o => o.description === action.payload).description;
            state.selectedProvinceOrState = initialState.selectedProvinceOrState;
            state.provinceOrState = initialState.provinceOrState;
            state.county = initialState.county;
            state.countyList = initialState.countyList;
            state.city = initialState.city;
            state.cityList = initialState.cityList;
        },
        handleChangeProvinceOrState: (state, action) => {
            if (action.payload?.value) {
                const selectedProvinceOrState = state.foreignStatesList.find(o => o.id === action.payload.id);
                state.selectedProvinceOrState = selectedProvinceOrState;
                state.provinceOrState.value = selectedProvinceOrState.description;
                state.provinceOrState.error = '';
                state.county = initialState.county;
                state.city = initialState.city;
            }
        },
        handleChangeCounty: (state, action) => {
            state.county.value = action.payload ?? '';
            state.county.error = '';
        },
        setCountyList: (state, action) => {
            state.countyList = action.payload;
        },
        handleChangeCity: (state, action) => {
            state.city.value = action.payload ?? '';
            state.city.error = '';
        },
        setCityList: (state, action) => {
            state.cityList = action.payload;
        },
        handleFillCountryAndState: (state, action) => {
            const selectedCountry = state.countryList.find(o => o.description === state.country.value);
            const selectedProvinceOrState = action.payload.find(o => o.description === state.provinceOrState.value);

            if (selectedCountry) {
                state.selectedCountry = selectedCountry;
            }

            if (selectedProvinceOrState) {
                state.selectedProvinceOrState = selectedProvinceOrState;
            }
        },
        setForeignStatesList: (state, action) => {
            state.foreignStatesList = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder.addMatcher(
            countryApi.endpoints.getCountryList.matchFulfilled, (state, action) => {
                state.countryList = action.payload;
            }
        )
        .addMatcher(
            organizationProfileApi.endpoints.updateOrganizationProfile.matchFulfilled, (state, action) => {
                state.currentProfileInfo.companyName = state.companyName.value;
                state.currentProfileInfo.phoneNumber = state.phoneNumber.value;
                state.currentProfileInfo.businessEin = state.businessEin.value;
                state.currentProfileInfo.addressLine = state.addressLine.value;
                state.currentProfileInfo.addressLine2 = state.addressLine2.value;
                state.currentProfileInfo.country = state.country.value;
                state.currentProfileInfo.provinceOrState = state.provinceOrState.value;
                state.currentProfileInfo.county = state.county.value;
                state.currentProfileInfo.city = state.city.value;
                state.currentProfileInfo.postalCode = state.postalCode.value;
            }
        )
    }
});

const errorTrackedFields = [
    "companyName",
    "phoneNumber",
    "businessEin",
    "addressLine",
    "addressLine2",
    "city",
    "provinceOrState",
    "county",
    "postalCode",
    "country",
];

export const selectOrganizationInfoHasError = (state) =>
    errorTrackedFields.some((field) => !!state.organizationProfileState[field]?.error);

export const {
    handleChange,
    handleModelErrors,
    handleGet,
    handleChangeCountry,
    handleChangeProvinceOrState,
    handleChangeCounty,
    handleChangeCity,
    handleFillCountryAndState,
    handleShowPageLeave,
    setForeignStatesList,
    setCountyList,
    setCityList
} = organizationProfileSlice.actions;

export default organizationProfileSlice.reducer;
